<template>
  <div>
    <Head :title="title" />

    <!-- Filter Card -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 mb-4 ticket-filters-card">
      <div class="flex flex-wrap gap-2 p-4 items-center justify-between border-b border-gray-100">
        <h2 class="text-base font-semibold text-gray-700">Late Tickets Report</h2>
        <div class="flex items-center gap-3">
          <span class="text-xs text-gray-400">{{ tickets.total }} tiket ditemukan</span>
          <select v-model="form.per_page" class="ticket-filter-select">
            <option value="10">10 / page</option>
            <option value="25">25 / page</option>
            <option value="50">50 / page</option>
            <option value="100">100 / page</option>
          </select>
        </div>
      </div>

      <div class="filters-body px-4 pb-4">
        <!-- Date Range -->
        <div class="flex flex-wrap gap-2 items-center pt-3">
          <label class="text-sm font-medium whitespace-nowrap text-gray-600">Filter Date:</label>
          <input v-model="form.date_from" type="date" class="ticket-filter-input" />
          <span class="text-sm text-gray-400">–</span>
          <input v-model="form.date_to" type="date" class="ticket-filter-input" />
          <button v-if="form.date_from || form.date_to" @click="clearDates" class="text-xs text-gray-400 hover:text-gray-600 underline">Clear</button>
        </div>

        <!-- Dropdown Filters -->
        <div class="flex flex-wrap gap-2 items-center pt-3">
          <label class="text-sm font-medium whitespace-nowrap text-gray-600">Filter By:</label>

          <select v-model="form.department_id" class="ticket-filter-select">
            <option value="">Department</option>
            <option v-for="d in departments" :key="d.id" :value="d.id">{{ d.name }}</option>
          </select>

          <select-input-filter
            v-model="form.user_id"
            placeholder="Requester"
            :on-input="searchRequesters"
            :items="requesters"
            class="ticket-filter-item"
            @focus="searchRequesters"
          />

          <select-input-filter
            v-model="form.assigned_to"
            placeholder="Assigned To"
            :on-input="searchAssignees"
            :items="assignees"
            class="ticket-filter-item"
            @focus="searchAssignees"
          />

          <button v-if="hasActiveFilters" @click="resetFilters" class="text-xs text-red-500 hover:text-red-700 underline ml-1">Reset All</button>
        </div>
      </div>
    </div>

    <!-- Table -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden report-table">
      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="text-left border-b border-gray-100 bg-gray-50">
              <th class="px-4 py-3 text-xs text-gray-500 uppercase tracking-wider font-semibold">#</th>
              <th class="px-4 py-3 text-xs text-gray-500 uppercase tracking-wider font-semibold cursor-pointer select-none" @click="sortBy('uid')">
                <span class="flex items-center gap-1">No. Ticket <sort-icon :sort="form.sort" :direction="form.direction" column="uid" /></span>
              </th>
              <th class="px-4 py-3 text-xs text-gray-500 uppercase tracking-wider font-semibold">Subject</th>
              <th class="px-4 py-3 text-xs text-gray-500 uppercase tracking-wider font-semibold">Department</th>
              <th class="px-4 py-3 text-xs text-gray-500 uppercase tracking-wider font-semibold">Requester</th>
              <th class="px-4 py-3 text-xs text-gray-500 uppercase tracking-wider font-semibold">Assigned To</th>
              <th class="px-4 py-3 text-xs text-gray-500 uppercase tracking-wider font-semibold cursor-pointer select-none" @click="sortBy('created_at')">
                <span class="flex items-center gap-1">Created <sort-icon :sort="form.sort" :direction="form.direction" column="created_at" /></span>
              </th>
              <th class="px-4 py-3 text-xs text-gray-500 uppercase tracking-wider font-semibold">Deadline</th>
              <th class="px-4 py-3 text-xs text-gray-500 uppercase tracking-wider font-semibold cursor-pointer select-none" @click="sortBy('updated_at')">
                <span class="flex items-center gap-1">Closed Date <sort-icon :sort="form.sort" :direction="form.direction" column="updated_at" /></span>
              </th>
              <th class="px-4 py-3 text-xs text-gray-500 uppercase tracking-wider font-semibold cursor-pointer select-none" @click="sortBy('diff')">
                <span class="flex items-center gap-1">Late <sort-icon :sort="form.sort" :direction="form.direction" column="diff" /></span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="tickets.data.length === 0">
              <td colspan="10" class="px-4 py-10 text-center text-gray-400 text-sm">Tidak ada data</td>
            </tr>
            <tr
              v-for="(ticket, index) in tickets.data"
              :key="ticket.id"
              class="border-t border-gray-100 hover:bg-gray-100 transition-colors"
            >
              <td class="px-4 py-3 text-sm text-gray-400">
                {{ (tickets.current_page - 1) * tickets.per_page + index + 1 }}
              </td>
              <td class="px-4 py-3">
                <a :href="route('tickets.edit', ticket.uid || ticket.id)" class="text-indigo-600 hover:underline font-mono text-sm font-medium">
                  #{{ ticket.uid }}
                </a>
              </td>
              <td class="px-4 py-3 text-sm text-gray-800 max-w-xs truncate" :title="ticket.subject">{{ ticket.subject }}</td>
              <td class="px-4 py-3 text-sm text-gray-600">{{ ticket.department?.name || '—' }}</td>
              <td class="px-4 py-3 text-sm text-gray-600">
                {{ ticket.user ? properName(ticket.user.first_name + ' ' + ticket.user.last_name) : '—' }}
              </td>
              <td class="px-4 py-3 text-sm text-gray-600">
                {{ ticket.assigned_to ? properName(ticket.assigned_to.first_name + ' ' + ticket.assigned_to.last_name) : '—' }}
              </td>
              <td class="px-4 py-3 text-sm text-gray-500">{{ formatDate(ticket.created_at) }}</td>
              <td class="px-4 py-3 text-sm text-red-600 font-medium">{{ formatDate(ticket.deadline) }}</td>
              <td class="px-4 py-3 text-sm text-gray-500">{{ formatDate(ticket.updated_at) }}</td>
              <td class="px-4 py-3">
                <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-700">
                  {{ ticket.diff }}
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="border-t border-gray-100 px-4 py-3">
        <pagination :links="tickets.links" />
      </div>
    </div>
  </div>
</template>

<script>
import { Head } from '@inertiajs/vue3'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import SelectInputFilter from '@/Shared/SelectInputFilter'
import pickBy from 'lodash/pickBy'
import throttle from 'lodash/throttle'
import moment from 'moment'

const SortIcon = {
  props: ['sort', 'direction', 'column'],
  template: `
    <svg xmlns="http://www.w3.org/2000/svg" class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
      <path v-if="sort === column && direction === 'asc'" stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" />
      <path v-else-if="sort === column && direction === 'desc'" stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" />
      <path v-else stroke-linecap="round" stroke-linejoin="round" d="M7 11l5-5 5 5M7 13l5 5 5-5" />
    </svg>
  `,
}

export default {
  components: { Head, Pagination, SortIcon, SelectInputFilter },
  layout: Layout,
  props: {
    title:       String,
    tickets:     Object,
    departments: Array,
    users:       Array,
    filters:     Object,
  },
  data() {
    return {
      requesters: this.users ?? [],
      assignees:  this.users ?? [],
      form: {
        department_id: this.filters.department_id ?? '',
        user_id:       this.filters.user_id       ?? '',
        assigned_to:   this.filters.assigned_to   ?? '',
        date_from:     this.filters.date_from      ?? '',
        date_to:       this.filters.date_to        ?? '',
        sort:          this.filters.sort           ?? 'created_at',
        direction:     this.filters.direction      ?? 'desc',
        per_page:      this.filters.per_page       ?? '25',
      },
    }
  },
  computed: {
    hasActiveFilters() {
      return this.form.department_id || this.form.user_id || this.form.assigned_to
          || this.form.date_from || this.form.date_to
    },
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get(this.route('reports'), pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    searchRequesters(e) {
      const q = (e?.target?.value ?? '').toLowerCase()
      this.requesters = q
        ? this.users.filter(u => u.name.toLowerCase().includes(q))
        : this.users
    },
    searchAssignees(e) {
      const q = (e?.target?.value ?? '').toLowerCase()
      this.assignees = q
        ? this.users.filter(u => u.name.toLowerCase().includes(q))
        : this.users
    },
    sortBy(column) {
      if (this.form.sort === column) {
        this.form.direction = this.form.direction === 'asc' ? 'desc' : 'asc'
      } else {
        this.form.sort = column
        this.form.direction = 'asc'
      }
    },
    clearDates() {
      this.form.date_from = ''
      this.form.date_to   = ''
    },
    resetFilters() {
      this.form.department_id = ''
      this.form.user_id       = null
      this.form.assigned_to   = null
      this.form.date_from     = ''
      this.form.date_to       = ''
    },
    formatDate(date) {
      if (!date) return '—'
      return moment(date).format('DD MMM YYYY, HH:mm')
    },
  },
}
</script>
